<template>
  <base-header :collapse-width="collapseWidth" :max-width="maxWidth">
    <template #header-start>
      <header-selector
        v-if="productId"
        :company-switch-label="companySwitchLabel"
        :company-switch-url="companySwitchUrl"
        :product-list="productList"
        :portal-switch-label="portalSwitchLabel"
        :portal-switch-url="portalSwitchUrl"
        :productId="productId"
        home-route-name="Home"
      />
      <header-product-indicator
        v-if="organization"
        :organization="organization"
        :product-id="productId"
        home-route-name="Home"
      />
    </template>

    <template #header-main="{ collapsed }">
      <header-navigation
        v-if="productId && !collapsed"
        :options="navigationOptions"
        class="header-navigation"
      />
      <div class="spacer"></div>
      <header-search
        v-if="contextId && productId"
        :context-id="contextId"
        :expanded="!collapsed"
        :product-id="productId"
        class="header-search"
      />
    </template>

    <template #header-end="{ collapsed }">
      <notification-indicator class="notifications" />

      <header-profile
        :collapsed="collapsed"
        :navigation="navigationOptions"
        :options="profileMenuOptions"
        :user="user"
      />
    </template>
  </base-header>
</template>

<script lang="ts">
import { defineComponent, watch } from 'vue';
import type { IMenuItem, IOrganization, IUser, IWorkflowV2 } from '@rready/sdk';
import {
  type IProductData,
  useProducts,
  useViewSettings
} from '@rready/composables';
import { AppNavigation } from '@rready/integration';
import { useWindowSize } from '@vueuse/core';
import { useAuthStore } from '../../store/authStore';
import { useContextStore } from '../../store/contextStore';
import { useProductStore } from '../../store/productStore';
import NotificationIndicator from '../notifications/NotificationIndicator.vue';

interface IProfileMenuOption {
  label: string;
  faIcon?: string;
  routeName: string;
  routeParams?: Record<string, unknown>;
}

export default defineComponent({
  components: {
    NotificationIndicator
  },
  provide() {
    return {
      quickSearchConfig: {
        users: 'SearchUserPage',
        tickets: 'SearchTicketPage',
        campaigns: 'SearchCampaignPage',
        portalUrl: AppNavigation.portalUrl((this as any).contextId),
        target: '_self'
      }
    };
  },
  props: {
    maxWidth: {
      type: Number,
      required: false
    }
  },
  data() {
    return {
      collapseWidth: 0
    };
  },
  mounted() {
    // Wait for the HeaderNavigation to be rendered
    setTimeout(() => {
      this.setCollapseWidth();
    }, 300);
  },
  computed: {
    user(): IUser {
      return useAuthStore().user?.raw;
    },
    organization(): IOrganization {
      return useContextStore().organization;
    },
    contextId(): string {
      return useContextStore().contextId!;
    },
    productId(): string {
      return useProductStore().productId!;
    },
    workflow(): IWorkflowV2 {
      return useProductStore().workflow;
    },
    productList(): IProductData[] {
      const { permission } = useAuthStore();
      const { productsData } = useProducts(this.organization);

      return productsData.value.filter((it) =>
        permission?.checkProductPermission(it.productId, ['USER_TICKET_READ'])
      );
    },
    portalSwitchLabel(): string {
      return this.$t('structure.site.portalAdmin').toString();
    },
    portalSwitchUrl(): string | undefined {
      if (!this.productId) {
        return undefined;
      }
      const hasAdminAccess = useAuthStore().permission?.checkPermission([
        `ADMIN_PORTAL_ACCESS`
      ]);
      if (!hasAdminAccess) {
        return undefined;
      }
      const base = AppNavigation.adminUrl(this.contextId);
      const url = new URL(this.productId, base);
      url.searchParams.set('context', this.contextId);
      return url.toString();
    },
    companySwitchLabel(): string {
      return this.$t('structure.header.labelSwitchCompany').toString();
    },
    companySwitchUrl(): string | undefined {
      const contexts = useAuthStore().token?.contexts ?? [];
      if (contexts.length > 1) {
        const base = AppNavigation.authUrl();
        const url = new URL('company', base);
        return url.toString();
      } else {
        return undefined;
      }
    },
    profileMenuOptions(): IProfileMenuOption[] {
      const options: IProfileMenuOption[] = [];
      if (this.productId) {
        const hasCreateTicketPermission =
          useAuthStore().permission?.checkPermission([
            `${this.productId.toUpperCase()}_USER_TICKET_CREATE`
          ]);
        if (hasCreateTicketPermission) {
          options.push({
            label: this.$t('menu.createTicket').toString(),
            faIcon: 'fal fa-plus-circle',
            routeName: 'CreateTicket'
          });
        }
        options.push({
          label: this.$t('menu.myProfile').toString(),
          faIcon: 'fal fa-user',
          routeName: 'UserProfile',
          routeParams: {
            userId: this.user.id
          }
        });
      }
      options.push({
        label: this.$t('structure.header.labelLogout').toString(),
        faIcon: 'fal fa-sign-out',
        routeName: 'LogoutPage'
      });

      return options;
    },
    navigationOptions(): IProfileMenuOption[] {
      const pageSettings = useViewSettings(this.workflow, 'app').page(
        'HeaderMenu'
      );
      const links: IMenuItem[] = pageSettings?.links ?? [];
      return links.map((it) => ({
        label: this.$res(it.resources)?.label || this.$t(it.title).toString(),
        routeName: it.value
      }));
    }
  },
  methods: {
    setCollapseWidth() {
      const { width } = useWindowSize();
      watch(
        width,
        () => {
          // Check if the search input is intersecting with the last navigation item
          const searchPos =
            this.$el.querySelector('.header-search')?.getBoundingClientRect()
              .left ?? 1;
          const menuPos =
            this.$el
              .querySelector('.header-navigation :last-child')
              ?.getBoundingClientRect().right ?? 0;
          const isIntersecting = searchPos <= menuPos + 25;
          if (isIntersecting) {
            this.collapseWidth = width.value;
          }
        },
        { immediate: true }
      );
    }
  }
});
</script>

<style lang="scss" scoped>
.header-navigation {
  margin: 0 12px;
}

.notifications {
  margin-right: 6px;
}
</style>
